import { useState } from 'react';
import { X } from 'lucide-react';
import { Settings } from '@/hooks/useSettings';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  settings: Settings | null;
  onSave: (settings: Settings) => void;
  deployments: string[];
  voices: string[];
};

export default function SettingsModal({
  isOpen,
  onClose,
  settings,
  onSave,
  deployments,
  voices,
}: Props) {
  const [mode, setMode] = useState(settings?.mode || deployments[0] || '');
  const [voice, setVoice] = useState(settings?.voice || voices[0] || '');

  const handleSave = () => {
    onSave({ mode, voice });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-md rounded-lg border border-gray-700 bg-gray-900 p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-400 transition-colors hover:text-white"
            aria-label="Close settings"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Mode Setting */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Model
            </label>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="w-full rounded bg-gray-800 px-3 py-2 text-sm text-white border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              {deployments.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>

          {/* Voice Setting */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Voice
            </label>
            <select
              value={voice}
              onChange={(e) => setVoice(e.target.value)}
              className="w-full rounded bg-gray-800 px-3 py-2 text-sm text-white border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              {voices.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>

          {/* Note */}
          <div className="rounded bg-gray-800 p-3 text-xs text-gray-300">
            <p className="font-semibold text-white mb-1">Note:</p>
            <p>Settings will take effect on your next session.</p>
          </div>
        </div>

        {/* Buttons */}
        <div className="mt-6 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="rounded px-4 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-500"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
